import { EventSettingsContextType } from '../context-providers/event-settings-context/event-settings-context';
import { PassportPageType } from '../api/custom-page.types';
import { CUSTOM_PAGE, EVENT, MeetingSlotsResponse } from '../api';
import { fetchMeetingSlots } from '../api/protected';
import {
  getBookAMeetingPath,
  getBookAMeetingPaths,
  isBookAMeetingPath,
  normalizeRoutePath
} from './urlHelper';

export const BOOK_A_MEETING_SOURCE = 'book-a-meeting';
export const BOOK_A_MEETING_SOURCE_PATH_PARAM = 'sourcePath';

const isBookAMeetingPageEnabled = (
  enabled: boolean,
  publicEnabled: boolean,
  isAuthenticated: boolean
): boolean => (isAuthenticated ? enabled : publicEnabled);

const meetingSlotsContainMeetingId = (data: MeetingSlotsResponse, meetingId: string): boolean => {
  const slots = [
    ...(data.meetingSlots ?? []),
    ...(data.groups ?? []).flatMap(group => group.meetingSlots ?? [])
  ];

  return slots.some(slot => slot.id === meetingId);
};

export type ResolveBookAMeetingOriginPathParams = {
  eventSettings: EventSettingsContextType;
  pathPrefix: string;
  isAuthenticated: boolean;
  searchParams?: URLSearchParams;
  historyState?: unknown;
};

/**
 * Resolves the Book a Meeting list page path from router state or calendar/deep-link query params.
 */
export const resolveBookAMeetingOriginPath = ({
  eventSettings,
  pathPrefix,
  isAuthenticated,
  searchParams,
  historyState
}: ResolveBookAMeetingOriginPathParams): string | null => {
  const paths = getBookAMeetingPaths(eventSettings, pathPrefix, isAuthenticated);
  if (!paths.length) {
    return null;
  }

  if (typeof historyState === 'string' && isBookAMeetingPath(historyState, eventSettings, pathPrefix, isAuthenticated)) {
    return historyState;
  }

  const source = searchParams?.get('source');
  if (source !== BOOK_A_MEETING_SOURCE) {
    return null;
  }

  const sourcePath = searchParams?.get(BOOK_A_MEETING_SOURCE_PATH_PARAM);
  if (sourcePath) {
    const slug = sourcePath.replace(/^\//, '');
    const matchingPage = eventSettings.customPages?.find(
      page =>
        page.type === PassportPageType.BOOK_A_MEETING &&
        (page.path || 'book-a-meeting') === slug &&
        isBookAMeetingPageEnabled(page.enabled, page.publicEnabled, isAuthenticated)
    );

    if (matchingPage) {
      return normalizeRoutePath(`/${matchingPage.path || 'book-a-meeting'}`, pathPrefix);
    }

    const normalizedSourcePath = normalizeRoutePath(
      sourcePath.startsWith('/') ? sourcePath : `/${sourcePath}`,
      pathPrefix
    );
    if (paths.includes(normalizedSourcePath)) {
      return normalizedSourcePath;
    }
  }

  return paths.length === 1 ? paths[0] : null;
};

/**
 * Finds which Book a Meeting page contains the given meeting slot when multiple pages exist.
 */
export const findBookAMeetingPathForMeetingId = async (
  eventId: string,
  meetingId: string,
  eventSettings: EventSettingsContextType,
  pathPrefix: string,
  isAuthenticated: boolean
): Promise<string | null> => {
  const paths = getBookAMeetingPaths(eventSettings, pathPrefix, isAuthenticated);
  if (!paths.length) {
    return null;
  }

  if (paths.length === 1) {
    return paths[0];
  }

  const bookAMeetingPages = eventSettings.customPages?.filter(
    page =>
      page.type === PassportPageType.BOOK_A_MEETING &&
      page.customPageId &&
      isBookAMeetingPageEnabled(page.enabled, page.publicEnabled, isAuthenticated)
  ) ?? [];

  const resolvedPaths = await Promise.all(
    bookAMeetingPages.map(async page => {
      try {
        const slotsResponse = await fetchMeetingSlots(
          EVENT,
          eventId,
          CUSTOM_PAGE,
          String(page.customPageId),
          'bookable'
        );

        if (meetingSlotsContainMeetingId(slotsResponse, meetingId)) {
          return normalizeRoutePath(`/${page.path || 'book-a-meeting'}`, pathPrefix);
        }
      } catch {
        return null;
      }

      return null;
    })
  );

  const matchedPath = resolvedPaths.find(path => path !== null);
  if (matchedPath) {
    return matchedPath;
  }

  return getBookAMeetingPath(eventSettings, pathPrefix, isAuthenticated);
};
